import classNames from 'classnames';
import moment from 'moment';
import 'moment/locale/es';
import React, { useCallback, useEffect, useState } from 'react';
import Tree from 'react-d3-tree';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import useResizableSidePanel from '../../../hooks/useResizableSidePanel';
import useFetch from '../../../hooks/useFetch';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import Page from '../../../layout/Page/Page';
import { AuthState } from '../../../redux/authSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from '../../../router/menu';
import { CorporationService } from '../../../services/corporation/corporationService';
import { CorporationsApiResponse } from '../../../type/entities/corporation-type';
import CorporationForm from '../CorporationForm';
import CorporationsFilters from './CorporationsFilters';

interface TreeNode {
    name: string;
    id: string;
    attributes?: any;
    children?: TreeNode[];
}

interface CorporationsListProps {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    nodeId: string | undefined;
    setNodeId: (value: string | undefined) => void;
    openFilters: boolean;
    setOpenFilters: any;
}


const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

const CorporationsList: React.FC<CorporationsListProps> = ({ isOpen, setIsOpen, nodeId, setNodeId, openFilters, setOpenFilters }) => {

    const { user }: AuthState = useSelector((state: RootState) => state.auth);
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const { filters, updateFilters, resetFilters } = useFiltersPR();
    const { width, height } = useWindowDimensions();
    const service = new CorporationService();

    const [corporations, setCorporations] = useState<TreeNode | undefined>(undefined);
    const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; nodeData: any | null; }>({ visible: false, x: 0, y: 0, nodeData: null });
    const [nodeStatus, setNodeStatus] = useState<boolean>(false);
    const {
        containerRef,
        isDesktop,
        isResizing,
        setIsResizing,
        mainStyle,
        panelStyle,
        dividerStyle,
    } = useResizableSidePanel({
        isOpen,
        defaultPanelWidth: 520,
        minPanelWidth: 120,
        minMainWidth: 120,
    });

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listCorporations({ ...filters, limit: 1000 });
        return response.getResponseData() as CorporationsApiResponse;
    }, [filters]));

    /**
     * Build an infinite-depth tree based on the new corporation links.
     * It uses `childLinks` (parent -> children) returned by the backend.
     * Includes cycle protection to avoid infinite loops on malformed data.
     */
    function buildTreeFromLinks(data: any[]): TreeNode[] {
        const corporationsById = new Map<string, any>();
        const childrenByParentId = new Map<string, any[]>();

        // Get all corporations by id and store them in a map
        for (const corporation of data) {
            if (corporation?.id) {
                corporationsById.set(corporation.id, corporation);
            }
        }

        // Get all child links by parent id and store them in a map
        for (const corporation of data) {
            const parentId = corporation?.id;
            const childLinks = corporation?.childLinks ?? [];
            if (!parentId || !Array.isArray(childLinks)) continue;

            for (const link of childLinks) {
                const child = link?.childCorporation;
                if (!child?.id) continue;
                const list = childrenByParentId.get(parentId) ?? [];
                list.push(child);
                childrenByParentId.set(parentId, list);
            }
        }

        // Get all roots (corporations without parent)
        const roots = data.filter((c: any) => {
            const parentLinks = c?.parentLinks ?? [];
            return c?.id && (!Array.isArray(parentLinks) || parentLinks.length === 0);
        });

        // Sort roots by name
        const sortByName = (a: any, b: any) => (a?.name || '').localeCompare(b?.name || '');
        roots.sort(sortByName);

        // Convert each root to a tree node
        const toNode = (corp: any, pathIds: Set<string>, level: string): TreeNode => {
            const id = corp.id as string;
            const isCycle = pathIds.has(id);

            const full = corporationsById.get(id) ?? corp;
            const corporationFunction = full?.corporationFunction;
            const functionLabel =
                corporationFunction === 'providing' ? 'Impartidora' :
                corporationFunction === 'organising' ? 'Organizadora' :
                corporationFunction === 'platform' ? 'Plataforma' :
                corporationFunction === 'services' ? 'Empresa de servicios' :
                'N/A';

            const nextPath = new Set(pathIds);
            nextPath.add(id);

            const rawChildren = childrenByParentId.get(id) ?? [];
            rawChildren.sort(sortByName);

            // Get all children of the current corporation using recursive function
            const children = isCycle
                ? []
                : rawChildren.map((child: any, index: number) =>
                    toNode(child, nextPath, `${level}.${index + 1}`)
                );

            return {
                name: full?.name || '',
                id,
                active: full?.active,
                iban: full?.iban,
                createdAt: full?.createdAt?.date,
                updatedAt: full?.updatedAt?.date,
                cif: full?.cif,
                registeredOffice: full?.registeredOffice,
                function: functionLabel,
                attributes: {
                    CIF: full?.cif,
                    Dirección: full?.registeredOffice,
                    Función: functionLabel,
                    ...(isCycle ? { Nota: 'Ciclo detectado' } : {}),
                },
                children,
                representativeName: full?.representativeName,
                representativeEmail: full?.representativeEmail,
                representativeTelephone: full?.representativeTelephone,
                postalCode: full?.postalCode,
                province: full?.province,
            } as any;
        };

        return roots.map((root: any, index: number) => toNode(root, new Set<string>(), `1.${index + 1}`));
    }

    /**
     * Generate the corporation tree structure
     */
    function generateCorporationTree(data: any) {
        return {
            name: user?.companyName || 'Attiva',
            id: '1',
            children: buildTreeFromLinks(data),
        };
    };

    /**
     * Handle node click event
     */
    const handleNodeClick = (nodeData: any) => {
        if (nodeData?.id === '1') return;

        if (userCan('edit_corporation', 'corporation')) {
            navigate(`${menuRoutes.corporations.path}/detail/${nodeData?.id}`);
        }
    };

    const handleNodeMouseOver = (nodeData: any, evt: any) => {
        if (nodeData?.data?.id === '1') return;

        // Get node position relative to the SVG container
        const { x, y } = evt.target.getBoundingClientRect();

        // Toggle tooltip visibility and update position
        setTooltip({
            visible: true,
            x: x + window.scrollX + 35,
            y: y + window.scrollY,
            nodeData: nodeData.nodeDatum as any,
        });
    };

    /**
     * Update corporation tree when data is fetched
     */
    useEffect(() => {
        if (data?.data.length > 0) {
            setCorporations(generateCorporationTree(data.data));
        } else {
            setCorporations(undefined);
        }
    }, [data, filters]);

    if (loading) return <Loader height={'70'} />;

    return (
        <Page container='fluid'>
            <div
                ref={containerRef}
                className={classNames('flex flex-col gap-3 static networkWrapper', {
                    'md:flex-row': isOpen,
                    'select-none': isResizing
                })}
            >
                <div
                    id="treeWrapper"
                    className={classNames('w-full relative', { 'hidden': !userCan('list_corporation', 'corporation') })}
                    style={mainStyle}
                >
                    {(data !== undefined && corporations)
                        ? (
                            <Tree
                                data={corporations}
                                orientation="vertical"
                                pathFunc={'step'}
                                translate={{ x: width / 3, y: height / 10 }}
                                //dimensions={{ width: width, height: height - 200 }}
                                separation={{ siblings: 2, nonSiblings: 2.5 }}
                                nodeSize={{ x: 180, y: 160 }}
                                renderCustomNodeElement={(nodeData: any) => {
                                    return (
                                        <g>
                                            {nodeData?.nodeDatum.id !== '1'
                                                ? (
                                                    <>
                                                        <circle
                                                            r={15}
                                                            style={{ fill: nodeData.nodeDatum.active ? '#D4EDD9' : '#ffc500' }}
                                                            onClick={() => handleNodeClick(nodeData.nodeDatum)}
                                                            onMouseOver={(evt: any) => handleNodeMouseOver(nodeData, evt)} onMouseOut={() => setTooltip({ ...tooltip, visible: false })}
                                                        />
                                                        <g className="rd3t-label">
                                                            <text className={nodeData.nodeDatum.active ? 'rd3t-label__title' : 'rd3t-label__title-disabled'} textAnchor="start" x={40}>
                                                                {truncateText(nodeData.nodeDatum.name, 22)} {nodeData.nodeDatum.active ? '' : ' (Inactiva)'}
                                                            </text>
                                                            <text className="rd3t-label__attributes">
                                                                <tspan x={40} dy="1.2em">CIF: {truncateText(nodeData.nodeDatum.cif, 15)}</tspan>
                                                                <tspan x={40} dy="1.2em">Dirección: {truncateText(nodeData.nodeDatum.registeredOffice, 15)}</tspan>
                                                                <tspan x={40} dy="1.2em">Función: {truncateText(nodeData.nodeDatum.function, 15)}</tspan>
                                                            </text>
                                                        </g>
                                                    </>
                                                )
                                                : (
                                                    <>
                                                        <circle
                                                            r={15}
                                                            style={{ fill: '#04ABD8' }}
                                                        />
                                                        <g className="rd3t-label">
                                                            <text className="rd3t-label__title" textAnchor="start" x={40}>
                                                                {nodeData.nodeDatum.name}
                                                            </text>
                                                        </g>
                                                    </>
                                                )
                                            }
                                        </g>
                                    );
                                }}
                                shouldCollapseNeighborNodes
                                collapsible={false}
                                enableLegacyTransitions // Remove for large trees. Works only if collapsible is true
                                hasInteractiveNodes
                                scaleExtent={{ min: 0.4, max: 3 }} // Zoom scale
                                rootNodeClassName="node__root"
                                branchNodeClassName="node__branch"
                                leafNodeClassName="node__leaf mt-10"
                            />
                        )
                        : (
                            <div className='flex justify-center items-center h-[calc(100vh-100px)]'>
                                <p className='text-gray-500'>No se han encontrado empresas fiscales</p>
                            </div>
                        )
                    }
                </div>

                {tooltip.visible && tooltip.nodeData !== null && (
                    <div
                        onMouseLeave={() => setTooltip({ ...tooltip, visible: false })}
                        style={{
                            position: 'absolute',
                            top: tooltip.y,
                            left: tooltip.x,
                            background: 'white',
                            border: '1px solid #ccc',
                            padding: '10px 15px',
                            borderRadius: '5px',
                            boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                            minWidth: '120px',
                            maxWidth: '340px',
                            zIndex: 1000
                        }}
                    >
                        <h4 className='font-bold text-gray-700 mb-2'>{tooltip.nodeData?.name}</h4>
                        <p className='text-sm font-medium text-gray-500'>CIF: {tooltip.nodeData?.cif}</p>
                        <p className='text-sm font-medium text-gray-500'>Dirección: {tooltip.nodeData?.registeredOffice}</p>
                        <p className='text-sm font-medium text-gray-500'>Función: {tooltip.nodeData?.function}</p>
                        <p className='text-sm font-medium text-gray-500'>IBAN: {tooltip.nodeData?.iban}</p>
                        <p className='text-sm font-medium text-gray-500'>Creación: {moment(tooltip.nodeData?.createdAt).format('L')}</p>
                        <p className='text-sm font-medium text-gray-500'>Actualización: {tooltip.nodeData?.updatedAt ? moment(tooltip.nodeData.updatedAt).format('L') : 'N/A'}</p>
                    </div>
                )}

                {isOpen && (
                    <>
                        {isDesktop && (
                            <div
                                role="separator"
                                aria-orientation="vertical"
                                className="hidden md:block w-2 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
                                style={dividerStyle}
                                onMouseDown={() => setIsResizing(true)}
                            />
                        )}

                        <div className='w-full asideTreeForm ps-0 md:ps-3' style={panelStyle}>
                            {isDesktop && (
                                <div className="hidden md:flex justify-end mb-2">
                                    <div
                                        className="w-10 h-1 rounded bg-gray-300 cursor-col-resize"
                                        onMouseDown={() => setIsResizing(true)}
                                        title="Redimensionar panel"
                                    />
                                </div>
                            )}
                            <CorporationForm id={nodeId} setNodeId={setNodeId} nodeStatus={nodeStatus} setNodeStatus={setNodeStatus} isOpen={isOpen} setIsOpen={setIsOpen} refetch={refetch} />
                        </div>
                    </>
                )}
            </div>

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Empresas'}>
                <div className="grid grid-cols-1 gap-6">
                    <CorporationsFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
                </div>
            </FilterDrawerView>
        </Page >
    );
};

export default CorporationsList;
